import { createContext, useContext, useMemo } from 'react';
import { HandleType } from '@/enums/handle-type.enum';
import { WorkflowEditorMode } from '@/enums/workflow-editor-mode.enum';
import { useWorkflowAppByWorkflowIdQuery } from '@/queries/useWorkflowApp';
import { useWorkflowStore } from '@/state/workflow.state';
import { useWorkflowEditorMode } from '../FlowComponents/WorkflowRightToolbar/workflowEditorMode.store';
import { useEdges, useFlowView, useNodes } from '../FlowContext';
import type { FlowView } from '../Views/FlowView';
import type { Edge } from '@/types/node';
import type { NodeId } from 'web';

/** Targets of at least one edge — O(edges) to build, O(1) membership checks. */
function getNodeIdsWithIncomingEdge(edges: Edge[]): Set<NodeId> {
  const incomingEdgeTargets = new Set<NodeId>();
  for (const edge of edges) {
    incomingEdgeTargets.add(edge.target);
  }
  return incomingEdgeTargets;
}

export const ASSET_OUTPUT_TYPES: HandleType[] = [
  HandleType.Image,
  HandleType.Video,
  HandleType.Audio,
  HandleType.ThreeDee,
];

/** Node types that cannot be workflow app outputs */
const NODE_TYPES_NOT_ELIGIBLE_FOR_APP_BUILDER_OUTPUT = new Set(['custom_group', 'stickynote', 'target', 'router']);

/** Stable empty set when App Builder mode is off — avoids new Set() per edges/nodes change invalidating Flow.jsx reactFlowNodes memo */
const EMPTY_ELIGIBLE_OUTPUT_NODE_IDS = new Set<NodeId>();

/**
 * Whether a node may be chosen as a workflow app canvas output:
 * - The node exists and is not a structural/UI-only type (groups, sticky notes, targets, routers).
 * - It has at least one incoming edge so the downstream result is wired from upstream data.
 * - It exposes at least one output handle whose type is an asset medium (image, video, audio, 3D), matching what app outputs surface.
 */
function isEligibleOutputNode(nodeId: NodeId, incomingEdgeTargets: ReadonlySet<NodeId>, flowView: FlowView) {
  const node = flowView.getNodeNullable(nodeId);
  if (!node || NODE_TYPES_NOT_ELIGIBLE_FOR_APP_BUILDER_OUTPUT.has(node.type ?? '')) {
    return false;
  }
  if (!incomingEdgeTargets.has(nodeId)) {
    return false;
  }
  return flowView.getOutputIds(nodeId).some((outputId) => {
    const outputType = flowView.nodeOutputType(nodeId, outputId);
    return outputType !== undefined && ASSET_OUTPUT_TYPES.includes(outputType);
  });
}

function useWorkflowAppOutputNodeIds(enabled: boolean = true) {
  const workflowId = useWorkflowStore((state) => state.recipe.id);
  const { data: workflowApp } = useWorkflowAppByWorkflowIdQuery(enabled ? workflowId : undefined);

  return useMemo(() => {
    return new Set((workflowApp?.draftOverrides.outputs ?? []).map((output) => output.nodeId));
  }, [workflowApp?.draftOverrides.outputs]);
}

export type AppBuilderState = ReturnType<typeof useAppBuilderState>;

export const AppBuilderStateContext = createContext<AppBuilderState | null>(null);

export function useAppBuilderContext(nodeId: NodeId) {
  const appBuilderContext = useContext(AppBuilderStateContext);

  return useMemo(
    () => ({
      isAppBuilderModeEnabled: appBuilderContext?.isAppBuilderModeEnabled ?? false,
      isEligibleOutputNode: appBuilderContext?.isEligibleOutputNode(nodeId) ?? false,
      isWorkflowAppOutputNode: appBuilderContext?.isWorkflowAppOutputNode(nodeId) ?? false,
    }),
    [appBuilderContext, nodeId],
  );
}

export function useAppBuilderState() {
  const { workflowEditorMode } = useWorkflowEditorMode();
  const isAppBuilderModeEnabled = workflowEditorMode === WorkflowEditorMode.AppBuilder;

  const nodes = useNodes();
  const edges = useEdges();
  const flowView = useFlowView();
  const workflowAppOutputNodeIds = useWorkflowAppOutputNodeIds(isAppBuilderModeEnabled);

  const eligibleOutputNodeIds = useMemo(() => {
    if (!isAppBuilderModeEnabled) {
      return EMPTY_ELIGIBLE_OUTPUT_NODE_IDS;
    }

    const incomingEdgeTargets = getNodeIdsWithIncomingEdge(edges);
    return new Set(
      nodes.filter((node) => isEligibleOutputNode(node.id, incomingEdgeTargets, flowView)).map((node) => node.id),
    );
  }, [edges, isAppBuilderModeEnabled, flowView, nodes]);

  return useMemo(
    () => ({
      isAppBuilderModeEnabled,
      eligibleOutputNodeIds,
      workflowAppOutputNodeIds,
      isEligibleOutputNode: (nodeId: NodeId) => eligibleOutputNodeIds.has(nodeId),
      isWorkflowAppOutputNode: (nodeId: NodeId) => workflowAppOutputNodeIds.has(nodeId),
    }),
    [eligibleOutputNodeIds, isAppBuilderModeEnabled, workflowAppOutputNodeIds],
  );
}
